// Imágenes en línea en notas/comentarios de ticket del CRM (capturas pegadas
// del portapapeles). Réplica del helper puro del CRM
// (crm/apps/web/src/features/tickets/inlineImages.ts).
//
// Modelo: al pegar una imagen en el composer se añade como ADJUNTO normal
// (pipeline base64 existente) y se inserta en el body un token estable que
// referencia el adjunto POR FILENAME (los adjuntos no tienen id hasta
// persistirse). El filename se genera único al pegar: `pasted-{ts}-{rand}.{ext}`.
//
// Al renderizar la nota, cada token se sustituye por un <img> cuyo src se
// obtiene por fetch→objectURL del endpoint de descarga autenticado, casando
// token↔adjunto persistido por filename.
//
// Formato del token (subconjunto de Markdown image, deliberadamente estricto):
//   ![texto](attachment:FILENAME)
// Solo `attachment:` como esquema; FILENAME es el nombre del adjunto (sin
// paréntesis para no romper el parseo). El resto del body es texto plano.

/** Prefijo de esquema del token; el resto es el filename del adjunto. */
export const ATTACHMENT_TOKEN_SCHEME = "attachment:";

/** ![alt](attachment:filename) — alt y filename sin `]`/`)` para un parseo simple. */
const TOKEN_RE = /!\[([^\]]*)\]\(attachment:([^)]+)\)/g;

const extFromContentType = (contentType: string): string => {
  const map: Record<string, string> = {
    "image/png": "png",
    "image/jpeg": "jpg",
    "image/jpg": "jpg",
    "image/gif": "gif",
    "image/webp": "webp",
    "image/bmp": "bmp",
    "image/svg+xml": "svg",
  };
  return map[contentType.toLowerCase()] ?? "png";
};

/** Nombre único para una imagen pegada; sirve de clave token↔adjunto. */
export function makePastedImageFilename(contentType: string): string {
  const rand = Math.random().toString(36).slice(2, 8);
  return `pasted-${Date.now()}-${rand}.${extFromContentType(contentType)}`;
}

/** Token que se inserta en el body para referenciar el adjunto por filename. */
export function inlineImageToken(filename: string, alt = "imagen"): string {
  return `![${alt}](${ATTACHMENT_TOKEN_SCHEME}${filename})`;
}

export interface NotePart {
  type: "text" | "image";
  /** type='text': el fragmento de texto. type='image': el alt del token. */
  value: string;
  /** type='image': filename del adjunto referenciado. */
  filename?: string;
}

/**
 * Trocea el body en partes de texto e imagen para renderizar. Es puro: no
 * resuelve el src (eso lo hace el componente casando `filename` con la lista
 * de adjuntos persistidos). Los tokens cuyo filename no case con ningún
 * adjunto los renderiza el componente como texto (fallback inocuo).
 */
export function parseNoteBody(body: string): NotePart[] {
  const parts: NotePart[] = [];
  let lastIndex = 0;
  for (const m of body.matchAll(TOKEN_RE)) {
    const start = m.index ?? 0;
    if (start > lastIndex) {
      parts.push({ type: "text", value: body.slice(lastIndex, start) });
    }
    parts.push({ type: "image", value: m[1] || "imagen", filename: m[2] });
    lastIndex = start + m[0].length;
  }
  if (lastIndex < body.length) {
    parts.push({ type: "text", value: body.slice(lastIndex) });
  }
  return parts;
}
